import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  input,
  OnInit,
  PLATFORM_ID,
  viewChild,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { Browser } from '@capacitor/browser';
import { ModalController } from '@ionic/angular/standalone';
import { CardType } from 'src/app/enums/card-type';
import { DocumentCategory } from 'src/app/interfaces/document-category';
import { Project } from 'src/app/interfaces/project';
import { ProjectDocument } from 'src/app/interfaces/project-document';
import { MapboxService } from 'src/app/services/mapbox.service';
import { ProjectDocumentService } from 'src/app/services/project-document.service';
import { WidgetService } from 'src/app/services/widget.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateTextDirective } from '../../../../directives/translate-text.directive';
import { CardComponent } from '../../../../components/cards/card/card.component';
import { InlineSVGModule } from 'ng-inline-svg-2';

import { MapComponent } from 'src/app/components/map/map.component';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { AnalyticsType } from 'src/app/enums/analytics-type';
import { bbox, pointOnFeature } from '@turf/turf';
import * as mapboxgl from 'mapbox-gl';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import { Router } from '@angular/router';
import { ProjectStore } from '../../project.store';
import { MediaSliderModalComponent } from 'src/app/components/media-slider-modal/media-slider-modal.component';
import { IonicModule } from '@ionic/angular';
import { DownloadService } from 'src/app/services/download.service';

@Component({
  standalone: true,
  selector: 'app-project-about-us',
  templateUrl: './project-about-us.component.html',
  imports: [
    InlineSVGModule,
    CardComponent,
    TranslateTextDirective,
    MapComponent,
    TranslateModule,
    IonicModule,
  ],
})
export class ProjectAboutUsComponent implements OnInit, AfterViewInit {
  private readonly documentService = inject(ProjectDocumentService);
  private readonly mapboxService = inject(MapboxService);
  private readonly widgetService = inject(WidgetService);
  private readonly modalCtrl = inject(ModalController);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly downloadService = inject(DownloadService);

  public readonly project = input.required<Project>();
  public readonly isModal = input<boolean>(false);
  public readonly mapContainer = viewChild('mapContainer', {
    read: ElementRef,
  });
  public documentCategories: DocumentCategory[];
  public mapShouldLoad: boolean = false;

  private readonly threshold: number = 20;
  private map: mapboxgl.Map;
  private mapMarkers: { project: Project; isActive: boolean }[] = [];

  CardType = CardType;

  static readonly extToMimeType = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    pdf: 'application/pdf',
    gif: 'image/gif',
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    png: 'image/png',
  };

  private readonly projectStore = inject(ProjectStore);
  private readonly platformId = inject(PLATFORM_ID);

  constructor() {}

  ngOnInit() {
    this.loadDocuments();
  }

  ngAfterViewInit() {
    this.initMapLoader();
  }

  private initMapLoader() {
    if (isPlatformServer(this.platformId)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          this.mapShouldLoad = true;
          observer.unobserve(this.mapContainer().nativeElement);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(this.mapContainer().nativeElement);
  }

  async loadDocuments() {
    this.documentCategories = await this.documentService.getCategories(
      this.project()
    );
    this.documentCategories.forEach((c) =>
      c.documents
        .filter((d) => !d.mimeType)
        .forEach((d) => (d.mimeType = this.determineMimeType(d)))
    );
  }

  dismissModal() {
    this.modalCtrl.dismiss();
  }

  async openModal(categoryIndex: number, documentIndex: number) {
    if (this.widgetService.getIsEmbedded()) return;

    this.analyticsService.logEvent(
      AnalyticsType.OPEN_ATTACHMENT,
      {},
      this.project()
    );

    const modal = await this.modalCtrl.create({
      component: MediaSliderModalComponent,
      componentProps: {
        attachments: this.documentCategories[categoryIndex].documents,
        startAtSlide: documentIndex,
        project: this.project(),
        withDownload: true,
      },
      htmlAttributes: {
        'aria-label': this.translateService.instant(
          'update.attachment.carousel'
        ),
      },
      canDismiss: true,
      cssClass: 'media-slider-modal',
    });

    modal.present();
  }

  downloadDocument(document: ProjectDocument) {
    if (this.widgetService.getIsEmbedded()) return;

    if (this.isYoutube(document)) {
      Browser.open({ url: 'https://youtu.be/' + document.videoId });
      return;
    }

    this.downloadService.downloadDocument(document, this.project());
  }

  isPdf(document: ProjectDocument) {
    return !!document.mimeType && document.mimeType.includes('pdf');
  }

  isImage(document: ProjectDocument) {
    return !!document.mimeType && document.mimeType.startsWith('image');
  }

  isVideo(document: ProjectDocument) {
    if (!document.mimeType) {
      return false;
    }
    if (document.mimeType.includes('youtube')) {
      return false;
    }

    return document.mimeType.startsWith('video');
  }

  isYoutube(document: ProjectDocument) {
    return !!document.mimeType && document.mimeType.includes('youtube');
  }

  determineMimeType(document: ProjectDocument): string {
    if (document.videoId) return 'video/youtube';

    const extension = document.document.split('.')[1];
    if (extension in ProjectAboutUsComponent.extToMimeType) {
      return ProjectAboutUsComponent.extToMimeType[extension];
    }
    return null;
  }

  public mapLoaded(map: mapboxgl.Map): void {
    this.map = map;
    this.mapMarkers = this.getMapMarkers();

    this.map.addSource('projects', {
      type: 'geojson',
      data: this.mapboxService.buildGeoJSON(
        this.mapMarkers.map((m) => m.project)
      ),
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 60,
    });

    const markerEntries: {
      marker: mapboxgl.Marker;
      bounds?: mapboxgl.LngLatBounds;
    }[] = [];

    this.map.once('idle', () => {
      for (const markerData of this.mapMarkers) {
        const { coords, bounds } = this.processShapeFile(markerData);
        const marker = this.createMarker(markerData, coords);
        markerEntries.push({ marker, bounds });
      }

      this.fitMapToBounds(markerEntries);
    });

    this.map.on('move', () => {
      for (const entry of markerEntries) {
        const el = entry.marker.getElement();
        el.style.display =
          entry.bounds && this.isShapeVisibleOnScreen(this.map, entry.bounds)
            ? 'none'
            : '';
      }
    });
  }

  private getMapMarkers() {
    const currentProject = this.project();
    const projects = [];

    if (currentProject.isParentProject) {
      projects.push(
        ...currentProject.childrenProjects.map((project) => ({
          project,
          isActive: true,
        }))
      );
    } else {
      const parentProject = currentProject.parentProject;

      if (parentProject) {
        projects.push(
          ...parentProject.childrenProjects.map((project) => ({
            project,
            isActive: false,
          }))
        );

        projects.push({
          project: parentProject,
          isActive: false,
        });
      }
    }

    projects.push({ project: currentProject, isActive: true });

    return projects;
  }

  private processShapeFile(markerData: {
    project: Project;
    isActive: boolean;
  }) {
    let coords: mapboxgl.LngLatLike = [
      markerData.project.locationLong,
      markerData.project.locationLat,
    ];
    let bounds: mapboxgl.LngLatBounds | undefined = undefined;

    if (!markerData.project.locationShapeFileData) {
      return { coords, bounds };
    }

    let shapefile: Feature | FeatureCollection;

    try {
      shapefile = JSON.parse(markerData.project.locationShapeFileData);
    } catch {
      return { coords, bounds };
    }

    this.mapboxService.addShapeFile(
      this.map,
      markerData.project.slug,
      shapefile,
      this.getShapeFileBeforeId(markerData)
    );

    if (this.hasPolygon(shapefile)) {
      bounds = this.getBoundsFromShapeFile(shapefile);
      this.handleInactiveOverlay(markerData);
      const [longitude, latitude] =
        pointOnFeature(shapefile).geometry.coordinates;
      coords = [longitude, latitude];
      this.addLabelLayer(markerData, coords);
      this.addPolygonEvents(markerData, bounds);
    }

    return { coords, bounds };
  }

  private getShapeFileBeforeId(markerData: {
    project: Project;
    isActive: boolean;
  }): string | undefined {
    if (!markerData.project.isParentProject) {
      return undefined;
    }

    return this.mapMarkers
      .filter((entry) => entry.project.id !== markerData.project.id)
      .map((entry) => `${entry.project.slug}-fill`)
      .find((layerId) => !!this.map.getLayer(layerId));
  }

  private hasPolygon(geo: Feature | FeatureCollection | Geometry): boolean {
    const geometries: Geometry[] = [];

    if (geo.type === 'FeatureCollection') {
      for (const feature of geo.features ?? []) {
        if (feature.geometry) {
          geometries.push(feature.geometry);
        }
      }
    } else if (geo.type === 'Feature') {
      if (geo.geometry) {
        geometries.push(geo.geometry);
      }
    } else {
      geometries.push(geo);
    }

    return geometries.some(
      (geometry) =>
        geometry.type === 'Polygon' || geometry.type === 'MultiPolygon'
    );
  }

  private getBoundsFromShapeFile(
    shapefile: Feature | FeatureCollection | Geometry
  ): mapboxgl.LngLatBounds | undefined {
    const box = bbox(shapefile);

    if (box.some((value) => !Number.isFinite(value))) {
      return undefined;
    }

    return new mapboxgl.LngLatBounds([box[0], box[1]], [box[2], box[3]]);
  }

  private handleInactiveOverlay(markerData: {
    project: Project;
    isActive: boolean;
  }) {
    if (!markerData.isActive) {
      const overlayId = `${markerData.project.slug}-inactive-overlay`;
      const sourceId = markerData.project.slug;
      if (!this.map.getLayer(overlayId)) {
        this.map.addLayer(
          {
            id: overlayId,
            type: 'fill',
            source: sourceId,
            layout: {},
            paint: {
              'fill-color': '#575757',
              'fill-opacity': 0.5,
            },
          },
          `${sourceId}-stroke`
        );
      }
      const strokeId = `${markerData.project.slug}-stroke`;
      if (this.map.getLayer(strokeId)) {
        this.map.setPaintProperty(strokeId, 'line-color', '#e9e9e9');
      }
    }
  }

  private addLabelLayer(
    markerData: { project: Project; isActive: boolean },
    coords: mapboxgl.LngLatLike
  ) {
    const labelSourceId = `${markerData.project.slug}-label`;
    const labelLayerId = `${markerData.project.slug}-label-layer`;

    if (!this.map.getSource(labelSourceId)) {
      this.map.addSource(labelSourceId, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: coords as [number, number],
              },
              properties: {
                label: markerData.project.name || markerData.project.slug,
              },
            },
          ],
        },
      });
    }

    if (!this.map.getLayer(labelLayerId)) {
      this.map.addLayer({
        id: labelLayerId,
        type: 'symbol',
        source: labelSourceId,
        layout: {
          'text-field': ['get', 'label'],
          'text-size': 14,
          'text-anchor': 'center',
          'text-justify': 'center',
        },
        paint: {
          'text-color': markerData.isActive ? '#000000' : '#666666',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.5,
        },
      });
    }
  }

  private addPolygonEvents(
    markerData: { project: Project; isActive: boolean },
    bounds?: mapboxgl.LngLatBounds
  ) {
    const fillLayerId = `${markerData.project.slug}-fill`;
    this.map.on('click', fillLayerId, (e) => {
      if (e.defaultPrevented) {
        return;
      }

      e.preventDefault();
      if (bounds) {
        this.map.fitBounds(bounds, { padding: 40, duration: 800 });
      }
      this.handleClick(markerData.project);
    });
    this.map.on('mouseenter', fillLayerId, () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });
    this.map.on('mouseleave', fillLayerId, () => {
      this.map.getCanvas().style.cursor = '';
    });
  }

  private createMarker(
    markerData: { project: Project; isActive: boolean },
    coords: mapboxgl.LngLatLike
  ) {
    const wrapper = document.createElement('div');
    wrapper.className = 'marker-wrapper';

    const el = document.createElement('div');
    el.className = 'marker' + (markerData.isActive ? '' : ' marker--grey');
    el.id = `marker-${markerData.project.id}`;

    const label = document.createElement('div');
    label.className = 'marker--label';
    label.innerText = markerData.project.name || markerData.project.slug;

    wrapper.appendChild(label);
    wrapper.appendChild(el);

    el.addEventListener('click', () => {
      this.handleClick(markerData.project);
    });

    return new mapboxgl.Marker(wrapper, { anchor: 'bottom' })
      .setLngLat(coords)
      .addTo(this.map);
  }

  private fitMapToBounds(
    markerEntries: { marker: mapboxgl.Marker; bounds?: mapboxgl.LngLatBounds }[]
  ) {
    const globalBounds = new mapboxgl.LngLatBounds();
    for (const entry of markerEntries) {
      globalBounds.extend(entry.marker.getLngLat());
      if (entry.bounds) {
        globalBounds.extend(entry.bounds.getNorthEast());
        globalBounds.extend(entry.bounds.getSouthWest());
      }
    }
    if (!globalBounds.isEmpty()) {
      this.map.fitBounds(globalBounds, {
        padding: 60,
        duration: 2000,
        maxZoom: 17,
      });
    }
  }

  private handleClick(project: Project) {
    this.projectStore.setProject(project);
    this.router.navigate(['projects', project.slug]);
  }

  private isShapeVisibleOnScreen(
    map: mapboxgl.Map,
    bounds: mapboxgl.LngLatBounds
  ): boolean {
    const ne = map.project(bounds.getNorthEast());
    const sw = map.project(bounds.getSouthWest());

    const width = Math.abs(ne.x - sw.x);
    const height = Math.abs(ne.y - sw.y);

    return width >= this.threshold && height >= this.threshold;
  }
}
